if (!customElements.get('mobile-sticky-purchase')) {
  customElements.define('mobile-sticky-purchase', class extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info[id^="MainProduct-"]');
      if (!this.productInfo || this.productInfo.dataset.originalSection || !window.IntersectionObserver) return;
      this.media = window.matchMedia('(max-width: 749px)');
      this.action = this.querySelector('[data-sticky-action]');
      this.label = this.querySelector('[data-sticky-label]');
      this.price = this.querySelector('[data-sticky-price]');
      this.schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          if (this.isConnected) this.sync();
        });
      };
      this.onViewportResize = () => {
        this.viewportChanged = true;
        this.schedule();
      };
      this.onActivate = () => this.activate();
      this.action.addEventListener('click', this.onActivate);
      this.media.addEventListener('change', this.schedule);
      document.addEventListener('focusin', this.schedule);
      document.addEventListener('focusout', this.schedule);
      window.addEventListener('sylavie:cart-busy', this.schedule);
      window.addEventListener('pageshow', this.schedule);
      window.addEventListener('resize', this.onViewportResize);
      window.visualViewport?.addEventListener('resize', this.onViewportResize);

      // Main form/price are authoritative, including transient locks and errors.
      this.stateObserver = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.stateObserver.observe(this.productInfo, {
        subtree: true, childList: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class', 'hidden', 'value',
          'data-size-selection-pending', 'style'],
      });
      // Apps may inject dialogs outside the product section. Attribute filtering
      // and one scheduled update coalesce changes without scroll polling.
      this.overlayObserver = new MutationObserver((records) => {
        if (records.some((record) => !this.contains(record.target))) this.schedule();
      });
      this.overlayObserver.observe(document.body, {
        subtree: true, childList: true, attributes: true,
        attributeFilter: ['class', 'style', 'open', 'hidden', 'aria-hidden', 'aria-modal', 'inert'],
      });
      this.resizeObserver = window.ResizeObserver ? new ResizeObserver(() => this.measure()) : null;
      this.resizeObserver?.observe(this);
      Promise.all(['product-form', 'product-info'].map((name) => customElements.whenDefined(name)))
        .then(() => { if (this.isConnected) this.schedule(); });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.stateObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.buttonObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.action?.removeEventListener('click', this.onActivate);
      this.media?.removeEventListener('change', this.schedule);
      document.removeEventListener('focusin', this.schedule);
      document.removeEventListener('focusout', this.schedule);
      window.removeEventListener('sylavie:cart-busy', this.schedule);
      window.removeEventListener('pageshow', this.schedule);
      window.removeEventListener('resize', this.onViewportResize);
      window.visualViewport?.removeEventListener('resize', this.onViewportResize);
    }

    getState() {
      const form = this.productInfo.productForm;
      const button = form?.submitButton;
      const picker = this.productInfo.variantSelectors;
      const selectSize = Boolean(form?.requiresSizeSelection && !picker?.hasCompleteSelection &&
        form.dataset.productSoldOut !== 'true');
      const busy = Boolean(form?.getAttribute('aria-busy') === 'true' ||
        button?.getAttribute('aria-disabled') === 'true' || button?.classList.contains('loading') ||
        window.CartMutations?.busy || window.CartMutations?.checkoutPending);
      const canPurchase = Boolean(button && !button.disabled && !busy &&
        form.variantIdInput?.value && !form.variantIdInput.disabled &&
        (!form.requiresSizeSelection || form.canSubmitSizeSelection()));
      return { form, button, picker, selectSize, busy, canPurchase };
    }

    sync() {
      const state = this.getState();
      const { form, button, selectSize, busy, canPurchase } = state;
      const eligible = Boolean(this.media.matches && button && !button.hidden &&
        this.productInfo.getClientRects().length && !this.productInfo.dataset.originalSection);
      this.hidden = !eligible;
      this.toggleAttribute('data-ready', eligible);
      if (!eligible) {
        this.setVisible(false);
        return;
      }
      if (this.mainButton !== button) {
        this.mainButton = button;
        this.pastPurchase = false;
        this.buttonObserver?.disconnect();
        this.buttonObserver = new IntersectionObserver(([entry]) => {
          this.updatePurchaseVisibility(entry);
          this.schedule();
        }, { threshold: 0 });
        this.buttonObserver.observe(button);
      }
      this.measure();
      if (this.viewportChanged) {
        this.viewportChanged = false;
        this.updatePurchaseVisibility({ boundingClientRect: button.getBoundingClientRect() });
      }
      const loading = !selectSize && (button.classList.contains('loading') || this.productInfo.abortController);
      const text = selectSize ? window.variantStrings.selectSize :
        loading ? this.dataset.loadingText : form.submitButtonText.textContent.trim();
      if (this.label.textContent !== text) this.label.textContent = text;
      this.action.disabled = selectSize ? Boolean(window.CartMutations?.busy || window.CartMutations?.checkoutPending) : !canPurchase;
      this.action.toggleAttribute('data-select-size', selectSize);
      this.action.setAttribute('aria-busy', String(busy && !selectSize));
      this.action.setAttribute('aria-controls', form.form.id);
      if (document.querySelector('cart-drawer') && !selectSize) this.action.setAttribute('aria-haspopup', 'dialog');
      else this.action.removeAttribute('aria-haspopup');

      const source = this.productInfo.querySelector(`[id="price-${this.dataset.sectionId}"]`);
      const priceNode = source?.querySelector('.price');
      const currentPrice = priceNode?.querySelector(priceNode.classList.contains('price--on-sale')
        ? '.price__sale .price-item--sale' : '.price__regular > .price-item--regular');
      const priceText = source && !source.classList.contains('hidden') &&
        !(form.getAttribute('aria-busy') === 'true' && !selectSize) ? currentPrice?.textContent.trim() || '' : '';
      if (this.price.textContent !== priceText) this.price.textContent = priceText;
      this.setVisible(Boolean(this.pastPurchase && !this.hasBlockingUi()));

      const error = form.querySelector('.product-form__error-message-wrapper:not([hidden])');
      if (this.attempted && error && !busy) {
        this.attempted = false;
        this.reveal(error);
      }
    }

    measure() {
      if (this.hidden) return;
      const height = Math.ceil(this.getBoundingClientRect().height);
      if (height === this.barHeight) return;
      this.barHeight = height;
      document.documentElement.style.setProperty('--mobile-sticky-purchase-height', `${height}px`);
    }

    viewportBounds(entry) {
      if (entry.rootBounds) return entry.rootBounds;
      // Embedded previews can omit rootBounds. DOM rects and visualViewport
      // offsets share layout-viewport coordinates; read them afresh on resize.
      const viewport = window.visualViewport;
      const top = viewport?.offsetTop || 0;
      return { top, bottom: top + (viewport?.height || document.documentElement.clientHeight || window.innerHeight) };
    }

    updatePurchaseVisibility(entry) {
      const rect = entry.boundingClientRect;
      this.pastPurchase = !entry.isIntersecting && rect.bottom > rect.top &&
        rect.bottom <= this.viewportBounds(entry).top;
    }

    hasBlockingUi() {
      if (window.CartMutations?.checkoutPending) return true;
      if (document.body.matches('.overflow-hidden, .overflow-hidden-mobile, .overflow-hidden-tablet, .modal-open, .welcome-popup-open')) {
        return true;
      }
      const inertContainer = this.closest('[inert]:not(mobile-sticky-purchase)');
      if (inertContainer) return true;
      if (document.activeElement?.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select, [contenteditable="true"]')) {
        return true;
      }
      return Array.from(document.querySelectorAll(
        'cart-drawer.active, menu-drawer details[open], header-drawer details[open], ' +
        '[role="dialog"][aria-modal="true"], dialog[open], modal-dialog[open], product-modal[open], ' +
        'details[open] > .modal__content'
      )).some((element) => {
        // Dawn keeps search content mounted inside details even when closed.
        if (element.closest('details:not([open]), dialog:not([open]), modal-dialog:not([open]), product-modal:not([open]), cart-drawer:not(.active)') ||
          element.closest('[hidden], [aria-hidden="true"], [inert]') || !element.getClientRects().length) return false;
        const style = getComputedStyle(element);
        if (style.visibility !== 'visible' || style.display === 'none') return false;
        // An explicitly open or interactive overlay must still block while fading.
        const explicitlyOpen = element.closest('[open], cart-drawer.active, welcome-popup.is-visible, international-checkout-modal.is-visible');
        return Boolean(explicitlyOpen || style.opacity !== '0' || style.pointerEvents !== 'none');
      });
    }

    setVisible(visible) {
      this.toggleAttribute('data-visible', visible);
      this.inert = !visible;
      this.setAttribute('aria-hidden', String(!visible));
      // Never leave keyboard focus inside a control that has just disappeared.
      if (!visible && this.contains(document.activeElement) && !this.hasBlockingUi()) {
        const destination = this.mainButton?.disabled ? document.getElementById('cart-icon-bubble') : this.mainButton;
        destination?.focus({ preventScroll: true });
      }
    }

    reveal(element) {
      if (!element) return;
      if (!element.matches('select, button, input')) element.setAttribute('tabindex', '-1');
      element.focus({ preventScroll: true });
      element.scrollIntoView({ block: 'center', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
    }

    activate() {
      if (!this.hasAttribute('data-visible') || this.hasBlockingUi()) return;
      const state = this.getState();
      if (window.CartMutations?.busy || window.CartMutations?.checkoutPending) return;
      if (state.selectSize) {
        const group = state.picker.querySelector('[data-size-selection-target]');
        this.reveal(group?.querySelector('select') || group);
        return;
      }
      if (!state.canPurchase) return;
      this.attempted = true;
      // requestSubmit preserves the only product-form handler and cart writer lane.
      state.form.form.requestSubmit(state.button);
      this.sync();
    }
  });
}
